import { useState, useMemo, useCallback } from "react"
import { PopOver, Icon, Flex, Text, DateFormat, notification, Tag, Empty, Spinner } from "components/common"
import { AiFillClockCircle } from "react-icons/ai"
import { MdVerifiedUser, MdCancel } from "react-icons/md"
import dayjs from "dayjs"
import { Separator } from "components/custom"
import { t } from "i18next"
import theme from "utils/themeVars"
import { useLazyGetTaskVerificationStatusQuery } from "features/tasks/api"
import { getDuration } from "utils/date"
import { CUSTOM_TIME_FORMAT } from "constants"

import styles from './meetingTaskStatus.module.less'


/** Overall meeting verification status — icons for trigger + popover header (`Icon` name keys). */
const TASK_STATUS_CONFIG = {
  verified: {
    icon: "MdVerifiedUser",
    color: "#21aa45",
    locationTitle: "Location Matched",
    locationDesc: "Tracked location matches with the reported meeting location.",
  },
  unverified: {
    icon: "MdCancel",
    color: "#e83b3b",
    locationTitle: "Location Mismatch",
    locationDesc: "Tracked location does not match with the reported meeting location.",
  },
  pending: { icon: "AiFillClockCircle", color: "#d6a700" },
}

/** Normalizes lookup strings (e.g. "Google Verified" / "Google-Verified" → "google_verified"). */
const normalizeLookupKey = value => String(value ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_")

const getStatusConfig = status => {
  const normalizedStatus = status?.toLowerCase?.()
  if (normalizedStatus === "verified") {
    return { icon: "FaRegCheckCircle", color: "#21aa45" }
  }
  if (normalizedStatus === "pending") {
    return { icon: "FaRegClock", color: "#d6a700" }
  }
  return {
    icon: "RxCrossCircled",
    color: "#e83b3b",
  }
}

const ALLOWED_SUB_VERIFICATION_KEYS = [
  "image",
  "location",
  "checkin",
  "google_verified",
  "manager_review",
  "audit_review",
]
const SUB_VERIFICATION_KEY_SET = new Set(ALLOWED_SUB_VERIFICATION_KEYS)
const SUB_VERIFICATION_KEY_ALIASES = {
  googlemeet: "google_verified",
  google_meet: "google_verified",
  googleverified: "google_verified",
  managerreview: "manager_review",
  auditreview: "audit_review",
  check_in: "checkin",
}

const getSubVerificationKey = check => {
  const rawKey = normalizeLookupKey(check?.key)
  if (SUB_VERIFICATION_KEY_SET.has(rawKey)) return rawKey
  if (SUB_VERIFICATION_KEY_ALIASES[rawKey]) return SUB_VERIFICATION_KEY_ALIASES[rawKey]

  const rawName = normalizeLookupKey(check?.name)
  if (SUB_VERIFICATION_KEY_SET.has(rawName)) return rawName
  if (SUB_VERIFICATION_KEY_ALIASES[rawName]) return SUB_VERIFICATION_KEY_ALIASES[rawName]
  return rawKey || rawName
}

/** Manual review pills (shown above system checks); normalized keys match `audit_review`, `manager_review`, etc. */
const MANUAL_REVIEW_TAG_KEYS = new Set(["audit_review", "manager_review"])

const isManualReviewTag = check => MANUAL_REVIEW_TAG_KEYS.has(getSubVerificationKey(check))

/** Icons for audit/manager tags — same palette as overall meeting status. */
const getManualReviewTagVisual = status => {
  const s = normalizeLookupKey(status) || "unverified"
  if (s === "verified") return { icon: "FaRegCheckCircle", color: "#21aa45" }
  if (s === "pending") return { icon: "FaRegClock", color: "#d6a700" }
  return { icon: "RxCrossCircled", color: "#e83b3b" }
}

const getManualReviewTagLabel = check => {
  const key = getSubVerificationKey(check)
  if (key === "audit_review") return t("Audit Verification")
  if (key === "manager_review") return t("Manager Verification")
  return check?.name ?? "—"
}

const MANUAL_REVIEW_TAG_PROPS = {
  verified: { type: "light", color: "#009F2B", style: { "--tag-bg": "#EDF8F0", padding: "4px 10px" } },
  pending: { type: "light", color: "#D6A700", style: { "--tag-bg": "#D6A700", padding: "4px 10px" } },
  unverified: { type: "light", color: "#E83A3A", style: { "--tag-bg": "#FEF2F2", padding: "4px 10px" } },
}

/** Icon + copy aligned with legacy `checkInStatus` / `googleMeetingStatus` / image & location helpers. */
const getSubVerificationCopy = check => {
  const nameKey = getSubVerificationKey(check)
  const status = normalizeLookupKey(check?.status) || "unverified"

  const fallbackCopy = () => {
    const s = getStatusConfig(status)
    return { icon: s.icon, color: s.color, description: null, descRaw: check?.remarks }
  }

  if (nameKey === "checkin") {
    if (status === "pending") {
      return {
        icon: "FaRegClock",
        color: "#d6a700",
        description: t("Check-In verification is in progress."),
        descRaw: null,
      }
    }
    const isAvailable = status === "verified"
    return {
      icon: isAvailable ? "FaRegCheckCircle" : "RxCrossCircled",
      color: isAvailable ? "#21aa45" : "#e83b3b",
      description: isAvailable
        ? t("A client check-in record was found for the reported meeting date.")
        : t("No client check-in record was found for the reported meeting date."),
      descRaw: null,
    }
  }

  if (nameKey === "google_verified") {
    if (status === "pending") {
      return {
        icon: "FaRegClock",
        color: "#d6a700",
        description: t("Google Meeting verification is in progress."),
        descRaw: null,
      }
    }
    const isVerified = status === "verified"
    return {
      icon: isVerified ? "FaRegCheckCircle" : "RxCrossCircled",
      color: isVerified ? "#21aa45" : "#e83b3b",
      description: isVerified
        ? t("A verified Google Meeting record was found for this meeting.")
        : t("No verified Google Meeting record was found for this meeting."),
      descRaw: null,
    }
  }

  if (nameKey === "image") {
    if (status === "pending") {
      return {
        icon: "FaRegClock",
        color: "#d6a700",
        description: t("Image Under Review."),
        descRaw: null,
      }
    }
    if (status === "verified") {
      return {
        icon: "FaRegCheckCircle",
        color: "#21aa45",
        description: t("Attached image confirms that the meeting took place."),
        descRaw: null,
      }
    }
    return {
      icon: "RxCrossCircled",
      color: "#e83b3b",
      description: t("Attached image does not meet meeting verification criteria."),
      descRaw: null,
    }
  }

  if (nameKey === "location") {
    if (status === "pending") {
      return {
        icon: "FaRegClock",
        color: "#d6a700",
        description: t("Location Under Review."),
        descRaw: null,
      }
    }
    const cfg = TASK_STATUS_CONFIG[status] || TASK_STATUS_CONFIG.unverified
    return {
      icon: status === "verified" ? "FaRegCheckCircle" : "RxCrossCircled",
      color: cfg.color,
      description: t(cfg.locationDesc),
      descRaw: null,
    }
  }

  return fallbackCopy()
}

const resolveTaskId = data => {
  return (data?.id)
}

const MeetingTaskStatus = ({ data }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [getTaskVerificationStatus, { data: verificationData, isFetching: isFetchingVerification }] =
    useLazyGetTaskVerificationStatusQuery()
  const taskId = resolveTaskId(data)
  /** Listing row fallback — only used until `/tasks/verificationStatus/:id` has loaded. */
  const listingFallbackStatus = normalizeLookupKey(
    data?.verificationStatus ?? (data?.verified ? "verified" : "unverified")
  )
  
  const verificationStatusRaw = verificationData?.overallVerificationStatus
  const verificationStatus =
    verificationData && verificationStatusRaw != null && verificationStatusRaw !== ""
      ? normalizeLookupKey(verificationStatusRaw)
      : listingFallbackStatus || "unverified"
  const currentStatus = TASK_STATUS_CONFIG[verificationStatus] || TASK_STATUS_CONFIG.unverified

  const handlePopoverToggle = visible => {
    setIsPopoverOpen(visible)
    if (visible && taskId) {
      getTaskVerificationStatus(taskId, false).catch(err => notification.error({ message: err?.message }))
    }
  }

  const handleLocationClick = useCallback(() => {
    const latitude = verificationData?.meetingLocationCoordinates?.latitude
    const longitude = verificationData?.meetingLocationCoordinates?.longitude
    if (latitude && longitude) {
      window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, "_blank", "noopener,noreferrer")
    }
  }, [verificationData])

  const renderOverallStatusIcon = useCallback(
    (size = 15) => {
      if (verificationStatus === "verified") {
        return <MdVerifiedUser color={currentStatus?.color} size={size} aria-hidden />
      }
      if (verificationStatus === "pending") {
        return <AiFillClockCircle color={currentStatus?.color} size={size} aria-hidden />
      }
      if (verificationStatus === "unverified") {
        return <MdCancel color={currentStatus?.color} size={size} aria-hidden />
      }
      return <Icon icon={currentStatus?.icon} color={currentStatus?.color} size={size} aria-hidden />
    },
    [verificationStatus, currentStatus]
  )

  const popoverTitle = useMemo(() => {
    const hasCoordinates = Boolean(
      verificationData?.meetingLocationCoordinates?.latitude && verificationData?.meetingLocationCoordinates?.longitude
    )
    const visitStart = verificationData?.startTime
    const visitEnd = verificationData?.endTime
    const hasVisitStartEnd = Boolean(visitStart && visitEnd)
    const meetingStartTime = dayjs(visitStart)
    const meetingEndTime = dayjs(visitEnd)
    const durationMinutesRaw =
      verificationData?.durationMinutes ?? verificationData?.visitDetails?.durationMinutes
    const durationLabel = hasVisitStartEnd
      ? durationMinutesRaw != null && durationMinutesRaw !== ""
        ? typeof durationMinutesRaw === "number"
          ? `${durationMinutesRaw} ${t("minutes")}`
          : String(durationMinutesRaw)
        : getDuration(meetingStartTime, meetingEndTime)
      : null

    return (
      <div className={styles.meetingTaskPopover}>
        <Flex align="center" wrap={false} className="mbe-4" style={{ gap: 6 }}>
          {renderOverallStatusIcon(16)}
          <Text className="Bold fs16">
            {verificationStatus === "verified"
              ? t("Verified Task")
              : verificationStatus === "pending"
                ? t("Pending Task")
                : t("Unverified Task")}
          </Text>
        </Flex>

        <Flex gap={6} align="center" className="mbe-4">
          <Icon icon="SlLocationPin" size={14} style={{ marginRight: 5 }}  />
          <Text className="fw-medium  gray-900 fs14" style={{ marginRight: 5, flexShrink: 0 }}>
            {verificationData?.taskSubLocation || t("Location")}
            {verificationData?.taskLocation ? ":" : ""}
          </Text>
          <Flex
            align="center"
            inline
            style={{
              cursor: hasCoordinates ? "pointer" : "default",
              color: theme["accent-color"],
              whiteSpace: "normal",
              overflowWrap: "break-word",
              maxWidth: "100%",
            }}
            onClick={handleLocationClick}
          >
            {verificationData?.taskLocation}
            {hasCoordinates && (
              <Icon icon="FiExternalLink" size={14}  style={{ marginLeft: 5 }} />
            )}
          </Flex>
        </Flex>
        {verificationData?.taskAddedDate && (
          <Flex align="center" gap={8} style={{ marginTop: 6, flexWrap: "wrap" }}>
            <DateFormat
              date={dayjs(verificationData.taskAddedDate)}
              showTimeWithTooltip={false}
              dateClassName="fw-medium gray-900"
            />
            {hasVisitStartEnd && (
              <>
                <Separator />
                <Icon icon="FaRegClock" size={14} color="#808080" style={{ marginRight: 4  }} />
                <Text className="gray-900 fw-medium fs-14" style={{ marginRight: 5, marginLeft: 5 }}>
                  {durationLabel || "—"}
                </Text>
                <Text className="gray-900 fw-medium fs14" style={{ direction: "ltr" }}>
                  ({meetingStartTime?.format(CUSTOM_TIME_FORMAT)} – {meetingEndTime?.format(CUSTOM_TIME_FORMAT)})
                </Text>
              </>
            )}
          </Flex>
        )}
      </div>
    )
  }, [verificationData, verificationStatus, handleLocationClick, renderOverallStatusIcon])

  const popoverContent = useMemo(() => {
    const subDetails = (verificationData?.subVerificationDetails ?? [])
      .filter(check => SUB_VERIFICATION_KEY_SET.has(getSubVerificationKey(check)))
    const manualReviewTags = subDetails.filter(isManualReviewTag)
    const systemChecks = subDetails.filter(c => !isManualReviewTag(c))

    if (subDetails.length === 0) {
      return (
        <div className="meeting-task-status-popover">
          <Flex justify="center" align="center" style={{ minHeight: 120, paddingBlock: 8 }}>
            <Empty
              description={t("No verification details available.")}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </Flex>
        </div>
      )
    }

    return (
      <div className="meeting-task-status-popover">
        {manualReviewTags.length > 0 && (
          <div className="mbe-4">
            {manualReviewTags.map((tag, index) => {
              const tagStatus = normalizeLookupKey(tag?.status) || "unverified"
              const modifier =
                tagStatus === "verified" ? "verified" : tagStatus === "pending" ? "pending" : "unverified"
              const visual = getManualReviewTagVisual(tag?.status)
              const tagProps = MANUAL_REVIEW_TAG_PROPS[modifier] || MANUAL_REVIEW_TAG_PROPS.unverified
              return (
                <Tag
                  key={`${getSubVerificationKey(tag)}-${index}`}
                  className="fs13"
                  {...tagProps}
                >
                  <Flex align="center" gap={4} wrap={false} style={{ columnGap: 4 }}>
                    <Icon icon={visual.icon} size={14} color={tagProps.color} />
                    <Text className="fs13" style={{ color: tagProps.color, lineHeight: 1.5 }}>
                      {getManualReviewTagLabel(tag)}
                    </Text>
                  </Flex>
                </Tag>
              )
            })}
          </div>
        )}
        {systemChecks.length > 0 && (
          <>
            <Text className="semiBold fs14  mbe-4">{t("System Verifications")}</Text>
            {systemChecks.map((check, index) => {
              const copy = getSubVerificationCopy(check)
              const descText =
                copy.descRaw != null && copy.descRaw !== ""
                  ? copy.descRaw
                  : copy.description ?? "—"
              return (
                <Flex
                  align="start"
                  gap="8px"
                  wrap={false}
                  className={index > 0 ? "mbe-8 mbs-4" : "mbe-8"}
                  key={`${getSubVerificationKey(check)}-${index}`}
                >
                  <span>
                    <Icon icon={copy.icon} color={copy.color} size={13} />
                  </span>
                  <div>
                    <span className="gray-900  fw-medium fs14">{check?.name ? t(check.name) : "—"}:</span>{" "}
                    <span className="gray-800 fw-normal fs14">{descText}</span>
                  </div>
                </Flex>
              )
            })}
          </>
        )}
      </div>
    )
  }, [verificationData])

  return (
    <PopOver
      trigger="click"
      title={popoverTitle}
      content={popoverContent}
      visible={isPopoverOpen && !isFetchingVerification}
      onVisibleChange={handlePopoverToggle}
      placement="right"
      overlayInnerStyle={{
        padding: 10,
        minWidth: 260,
        maxWidth: 430,
        borderRadius: "8px",
        wordBreak: "break-word",
      }}
    >
      <span className={styles.meetingTaskStatusTrigger} onClick={() => setIsPopoverOpen(true)}>
        {isFetchingVerification ? <Spinner size="small" /> : renderOverallStatusIcon(15)}
      </span>
    </PopOver>
  )
}

export default MeetingTaskStatus
